import { createFocusTrap } from 'focus-trap';
import { pubsub } from '@services';

export class MegaMenuDropdown extends HTMLElement {
  static activePane = null;

  static {
    this.initializeControllers();
  }

  static isDesktop() {
    return window.matchMedia('(min-width: 768px)').matches;
  }

  static initializeControllers() {
    document.querySelectorAll('[data-menu-pane-controller]').forEach((controller) => {
      if (controller.hasAttribute('data-menu-pane-initialized')) return;

      const paneId = controller.getAttribute('aria-controls');

      if (!paneId) return;

      controller.setAttribute('data-menu-pane-initialized', 'true');

      const isDesktop = this.isDesktop();

      if (isDesktop) {
        controller.addEventListener('mouseenter', () => {
          const pane = document.getElementById(paneId);
          if (!pane) return;
          pane.cancelClose();
          pane.handleAction('open');
        });

        controller.addEventListener('mouseleave', (event) => {
          const pane = document.getElementById(paneId);
          if (!pane) return;
          const relatedTarget = event.relatedTarget;
          if (relatedTarget && (relatedTarget === pane || pane.contains(relatedTarget))) return;
          pane.scheduleClose();
        });
      } else {
        // Mobile: Always prevent default navigation and only toggle menu
        controller.addEventListener('click', (event) => {
          // Double-check we're still on mobile/touch device
          if (this.isDesktop()) return;

          const pane = document.getElementById(paneId);
          if (!pane) return;

          // On mobile, always prevent navigation and only open/close menu
          event.preventDefault();
          pane.handleAction('toggle');
        });
      }
    });
  }

  connectedCallback() {
    this.closeTimeout = null;
    if (!this.hasAttribute('aria-expanded')) this.setAttribute('aria-expanded', 'false');

    this.trap = createFocusTrap(this, {
      allowOutsideClick: true,
      initialFocus: false,
      returnFocusOnDeactivate: true,
      fallbackFocus: this,
    });

    this.unsubscribeEscapeKey = pubsub.subscribe('key:escape', () => this.close());

    this.addEventListener('mouseenter', () => this.cancelClose());
    this.addEventListener('mouseleave', () => this.scheduleClose());
  }

  disconnectedCallback() {
    this.unsubscribeEscapeKey?.();
    if (this.closeTimeout) clearTimeout(this.closeTimeout);

    // Clear active pane if it's this element
    if (MegaMenuDropdown.activePane === this) {
      MegaMenuDropdown.activePane = null;
    }
  }

  handleAction(action) {
    if (action === 'open') this.open();
    else if (action === 'close') this.close();
    else this.toggle();
  }

  toggle() {
    this.getAttribute('aria-expanded') === 'true' ? this.close() : this.open();
  }

  open() {
    // Immediately close any other active pane
    if (MegaMenuDropdown.activePane && MegaMenuDropdown.activePane !== this) {
      MegaMenuDropdown.activePane.close();
    }

    if (this.getAttribute('aria-expanded') === 'true') return;
    this.cancelClose();
    this.setAttribute('aria-expanded', 'true');
    this.updateControllers();

    // Only activate trap if there are tabbable elements
    const tabbableElements = this.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (tabbableElements.length > 0) {
      try {
        this.trap.activate();
      } catch (error) {
        console.warn('Failed to activate focus trap:', error);
      }
    }

    // Set as active pane
    MegaMenuDropdown.activePane = this;
  }

  close() {
    if (this.getAttribute('aria-expanded') === 'false') return;
    this.cancelClose();

    // Only deactivate trap if it's currently active
    try {
      this.trap.deactivate();
    } catch {
      // Trap was not active, continue with closing
    }

    this.setAttribute('aria-expanded', 'false');
    this.updateControllers();

    // Clear active pane if it's this element
    if (MegaMenuDropdown.activePane === this) {
      MegaMenuDropdown.activePane = null;
    }
  }

  scheduleClose() {
    if (this.getAttribute('aria-expanded') === 'false') return;
    this.cancelClose();
    this.closeTimeout = setTimeout(() => this.close(), 200);
  }

  cancelClose() {
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }
  }

  updateControllers() {
    document
      .querySelectorAll(`[aria-controls="${this.id}"]`)
      .forEach((el) => el.setAttribute('aria-expanded', this.getAttribute('aria-expanded')));
  }
}

customElements.define('mega-menu-dropdown', MegaMenuDropdown);
